import _ from 'lodash';
import React, {useContext, useEffect} from 'react';
import {useLocation, useParams} from 'react-router-dom';
import Select from 'react-select';
import {DispatchOrder} from '../../types/DispatchOrder';
import {DispatchOrderStatus} from '../../types/DispatchOrderStatus';
import {NoActivityReport} from '../../types/NoActivityReport';
import {GlobalContext} from "../admin-frame/AdminFrame";
import {ApiUtility} from "../common/ApiUtility";
import {AuthUtility} from "../common/AuthUtility";
import {SelectOption} from "../common/CommonUIElementTypes";
import {ConditionalRenderer} from "../common/ConditionalRenderer";
import {ConfirmDeleteButton} from "../common/ConfirmDeleteButton";
import {Constants} from "../common/Constants";
import {DateControl} from "../common/DateControl";
import {DateUtility} from '../common/DateUtility';
import {DelayedSearchBar} from "../common/DelayedSearchBar";
import {ActivityListItemModelsByUser, DispatchOrdersUtility} from "../common/DispatchOrdersUtility";
import {useRenderableDataStore} from "../common/hooks/useRenderableDataStore";
import {MessageDisplayerUtility} from "../common/MessageDisplayerUtility";
import {OverlayLoadingIndicator} from "../common/OverlayLoadingIndicator";
import {PhoneUtility} from "../common/PhoneUtility";
import {SelectUtility} from "../common/SelectUtility";
import {UrlUtility} from "../common/UrlUtility";
import {DispatchOrderListRow} from "./DispatchOrderListRow";

interface DispatchOrdersListPageURLQueryParams {
    searchText?: string;
    dateString?: string;
    statusId?: string;
    jobId?: string;
    brokerId?: string;
    foremanUserId?: string;
}

export const DispatchOrdersListPage = function () {
    const context = useContext(GlobalContext);
    const urlBase = `/${Constants.routes.dispatch.pathSegment}`;
    const dataStore = useRenderableDataStore({
        lastRefreshTimestamp: undefined,
        dispatchOrdersList: [],
        noActivityReportsList: [],
        loading: true,
        dateString: DateUtility.jsDateToURLDate(new Date()),
        searchText: '',
        filterStatusId: null,
        filterJobId: null,
        filterBrokerId: null,
        renderedFilterBrokerId: null,
        filterForemanUserId: null,
        statusOptions: [] as SelectOption[],
        jobOptions: [] as SelectOption[],
        brokerOptions: [] as SelectOption[],
        foremanOptions: [] as SelectOption[],

        prevUrlQueryString: null,
        prevParams: null,
    })
    const currentUserCanDeleteNoActivityReport = AuthUtility.userHasOneOfThesePermissions(
        [Constants.permissionNames.noActivityReportDelete]
    );
    const location = useLocation();
    const routeParams = useParams();

    // Update state if URL changes.
    if (
        (location && location.search) !== dataStore.data().prevUrlQueryString ||
        routeParams !== dataStore.data().prevParams
    ) {
        let searchText = '';

        let queryParams: DispatchOrdersListPageURLQueryParams = UrlUtility.getQueryParamsFromSearchString(location.search);
        if (queryParams.searchText) {
            searchText = queryParams.searchText;
        }

        let dateString = dataStore.data().dateString;
        if (queryParams.dateString) {
            dateString = queryParams.dateString;
        }

        let filterStatusId: any = null;
        if (queryParams.statusId) {
            filterStatusId = queryParams.statusId;
        }

        let filterJobId: any = null;
        if (queryParams.jobId) {
            filterJobId = queryParams.jobId;
        }

        let filterBrokerId: any = null;
        if (queryParams.brokerId) {
            filterBrokerId = queryParams.brokerId;
        }

        let filterForemanUserId: any = null;
        if (queryParams.foremanUserId) {
            filterForemanUserId = queryParams.foremanUserId;
        }

        dataStore.updateAndRender({
            searchText: searchText,
            dateString: dateString,
            filterStatusId: filterStatusId,
            filterJobId: filterJobId,
            filterBrokerId: filterBrokerId,
            filterForemanUserId: filterForemanUserId,
            prevUrlQueryString: location.search,
            prevParams: routeParams,
        })
    }

    // Trigger reload of list data on mount and if certain values change in state.
    useEffect(
        function () {
            reloadListDebounced();
        },
        [
            '' + dataStore.data().searchText,
            '' + dataStore.data().dateString,
            '' + dataStore.data().filterStatusId,
            '' + dataStore.data().filterJobId,
            '' + dataStore.data().filterBrokerId,
            '' + dataStore.data().filterForemanUserId
        ]
    );

    // When the URL references a job id that's not in the active-jobs dropdown (because the job has gone inactive),
    // fetch the single job and inject a synthetic option so the filter still displays its name (with "(Inactive)" suffix).
    useEffect(
        function () {
            let storeData: any = dataStore.data();
            if (!storeData.lastRefreshTimestamp || !storeData.filterJobId) {
                return;
            }
            let hasMatchingOption = storeData.jobOptions.some(
                (option: SelectOption) => '' + option.value === '' + storeData.filterJobId
            );
            if (hasMatchingOption) {
                return;
            }
            ApiUtility.getJob(storeData.filterJobId)
                .then(function (job: any) {
                    if (!job) {
                        return;
                    }
                    dataStore.updateAndRender({
                        jobOptions: [
                            ...dataStore.data().jobOptions,
                            {value: job.id, label: job.display_name}
                        ]
                    });
                })
                .catch(function (error) {
                    console && console.error(error);
                });
        },
        [
            '' + dataStore.data().filterJobId,
            '' + dataStore.data().lastRefreshTimestamp
        ]
    );

    const reloadListDebounced = _.debounce(() => {
        let dataStoreUpdate: any = {};
        // snapshot the filter this reload is fetching for, so the client-side row filter reads the value the displayed list was fetched with, not a filter whose list is still in flight
        let filterBrokerIdForThisReload = dataStore.data().filterBrokerId;
        dataStore.updateAndRender({loading: true})

        ApiUtility.getDispatchOrdersListForDate(
            dataStore.data().dateString,
            dataStore.data().searchText,
            dataStore.data().filterStatusId,
            dataStore.data().filterJobId,
            dataStore.data().filterBrokerId,
            dataStore.data().filterForemanUserId
        )
            .then(function (listData) {
                dataStoreUpdate = {
                    lastRefreshTimestamp: (new Date()).getTime(),
                    dispatchOrdersList: listData.dispatchOrdersList,
                    noActivityReportsList: listData.noActivityReportsList,
                    renderedFilterBrokerId: filterBrokerIdForThisReload,
                    loading: false
                }

                // Fetch data for filter dropdowns one time (if option lists are empty).
                return new Promise<void>(function (resolve, reject) {
                    if (
                        dataStore.data().statusOptions.length === 0 ||
                        dataStore.data().jobOptions.length === 0 ||
                        dataStore.data().brokerOptions.length === 0 ||
                        dataStore.data().foremanOptions.length === 0
                    ) {
                        return ApiUtility.getDispatchOrderStatuses()
                            .then(function (statusesList: DispatchOrderStatus[]) {
                                dataStoreUpdate.statusOptions = SelectUtility.modelArrayToSelectOptions(
                                    statusesList, 'id', 'name'
                                )

                                return ApiUtility.getJobsForDropdown()
                            })
                            .then(function (jobsList: any[]) {
                                dataStoreUpdate.jobOptions = SelectUtility.modelArrayToSelectOptions(
                                    jobsList, 'id', 'label'
                                )

                                return ApiUtility.getBrokersForDropdown()
                            })
                            .then(function (brokersList: any[]) {
                                dataStoreUpdate.brokerOptions =
                                    SelectUtility.modelArrayToSelectOptions(brokersList)

                                return ApiUtility.getForemenForDropdown()
                            })
                            .then(function (foremenList: any[]) {
                                dataStoreUpdate.foremanOptions = SelectUtility.modelArrayToSelectOptions(
                                    foremenList,
                                    'id',
                                    undefined,
                                    (foreman: any) => foreman.person.name
                                )

                                resolve()
                            })
                            .catch(reject)

                    } else {
                        resolve()
                    }
                });
            })
            .then(function () {
                dataStore.updateAndRender(dataStoreUpdate);
            })
            .catch(function (error) {
                dataStore.updateAndRender({...dataStoreUpdate, loading: false});
                console && console.error(error);
                MessageDisplayerUtility.error(`An error occurred while loading the dispatch orders list page.`);
            });
    }, Constants.debounceTimeoutMs);

    function onDeleteNoActivityReport(noActivityReportId: number) {
        dataStore.updateAndRender({loading: true});
        ApiUtility.deleteNoActivityReport(noActivityReportId)
            .then(function () {
                MessageDisplayerUtility.success("Successfully deleted no-activity report.");
                reloadListDebounced();
            })
            .catch(function (error) {
                dataStore.updateAndRender({loading: false});
                console && console.error(error);
                MessageDisplayerUtility.error("An error occurred while deleting the no-activity report.");
            });
    }

    function goToAddDispatchOrderPage() {
        context.navigator.navigateTo(`${urlBase}/dispatch-orders/add${location.search}`);
    }

    function goToUpdateDispatchOrderPage(dispatchOrder: DispatchOrder) {
        let from = encodeURIComponent(location.pathname + location.search);
        context.navigator.navigateTo(`${urlBase}/dispatch-orders/${dispatchOrder.id}?from=${from}`);
    }

    function handleStatusChange(option: SelectOption | null) {
        let currentQueryParams: any = UrlUtility.getQueryParamsFromSearchString(location.search);
        let updatedQueryParams: any = {...currentQueryParams};
        updatedQueryParams.statusId = option?.value || null;
        let queryString = UrlUtility.getUrlQueryStringFromQueryParamsObject(updatedQueryParams);
        context.navigator.navigateTo(urlBase + queryString);
    }

    function handleJobChange(option: SelectOption | null) {
        let currentQueryParams: any = UrlUtility.getQueryParamsFromSearchString(location.search);
        let updatedQueryParams: any = {...currentQueryParams};
        updatedQueryParams.jobId = option?.value || null;
        let queryString = UrlUtility.getUrlQueryStringFromQueryParamsObject(updatedQueryParams);
        context.navigator.navigateTo(urlBase + queryString);
    }

    function handleBrokerChange(option: SelectOption | null) {
        let currentQueryParams: any = UrlUtility.getQueryParamsFromSearchString(location.search);
        let updatedQueryParams: any = {...currentQueryParams};
        updatedQueryParams.brokerId = option?.value || null;
        let queryString = UrlUtility.getUrlQueryStringFromQueryParamsObject(updatedQueryParams);
        context.navigator.navigateTo(urlBase + queryString);
    }

    function handleForemanChange(option: SelectOption | null) {
        let currentQueryParams: any = UrlUtility.getQueryParamsFromSearchString(location.search);
        let updatedQueryParams: any = {...currentQueryParams};
        updatedQueryParams.foremanUserId = option?.value || null;
        let queryString = UrlUtility.getUrlQueryStringFromQueryParamsObject(updatedQueryParams);
        context.navigator.navigateTo(urlBase + queryString);
    }

    function handleSearchChange(updatedSearchText: string) {
        let currentQueryParams: any = UrlUtility.getQueryParamsFromSearchString(location.search);
        let updatedQueryParams: any = {...currentQueryParams};
        updatedQueryParams.searchText = updatedSearchText || undefined;
        let queryString = UrlUtility.getUrlQueryStringFromQueryParamsObject(updatedQueryParams);
        context.navigator.navigateTo(urlBase + queryString);
    }

    function handleFilterDateChange(date: Date | null) {
        if (date) {
            let selectedDateString = DateUtility.jsDateToURLDate(date);
            if (dataStore.data().dateString !== selectedDateString) {
                let currentQueryParams: any = UrlUtility.getQueryParamsFromSearchString(location.search);
                let updatedQueryParams: any = {...currentQueryParams};
                updatedQueryParams.dateString = selectedDateString;
                let queryString = UrlUtility.getUrlQueryStringFromQueryParamsObject(updatedQueryParams);
                context.navigator.navigateTo(`${urlBase}${queryString}`);
            }
        }
    }

    function handlePreviousDate() {
        let previousDateString = DateUtility.jsDateToURLDate(
            DateUtility.urlDateToMoment(dataStore.data().dateString).subtract(1, 'days').toDate()
        );
        let currentQueryParams: any = UrlUtility.getQueryParamsFromSearchString(location.search);
        let updatedQueryParams: any = {...currentQueryParams};
        updatedQueryParams.dateString = previousDateString;
        let queryString = UrlUtility.getUrlQueryStringFromQueryParamsObject(updatedQueryParams);
        context.navigator.navigateTo(`${urlBase}${queryString}`);
    }

    function handleNextDate() {
        let nextDateString = DateUtility.jsDateToURLDate(
            DateUtility.urlDateToMoment(dataStore.data().dateString).add(1, 'days').toDate()
        );
        let currentQueryParams: any = UrlUtility.getQueryParamsFromSearchString(location.search);
        let updatedQueryParams: any = {...currentQueryParams};
        updatedQueryParams.dateString = nextDateString;
        let queryString = UrlUtility.getUrlQueryStringFromQueryParamsObject(updatedQueryParams);
        context.navigator.navigateTo(`${urlBase}${queryString}`);
    }

    function renderDispatchOrdersListDisplay() {
        let activityListItemModelsByUser: ActivityListItemModelsByUser = DispatchOrdersUtility.getActivityListItemModelsByUser(
            dataStore.data().dispatchOrdersList,
            dataStore.data().noActivityReportsList
        );

        let userRows: React.ReactNode[] = [];
        for (let i = 0; i < _.keys(activityListItemModelsByUser).length; i++) {
            let userId = parseInt(_.keys(activityListItemModelsByUser)[i]);
            let activityListItemModelsForUser = activityListItemModelsByUser[userId];
            let dispatchOrdersForUserDisplay = []
            for (let j = 0; j < activityListItemModelsForUser.dispatchOrders.length; j++) {
                let dispatchOrder = activityListItemModelsForUser.dispatchOrders[j];
                dispatchOrdersForUserDisplay.push(
                    <DispatchOrderListRow
                        key={`dispatch-order-${j}`}
                        dispatchOrder={dispatchOrder}
                        onClickDispatchOrderListRow={() => goToUpdateDispatchOrderPage(dispatchOrder)}
                        filterBrokerId={dataStore.data().renderedFilterBrokerId}
                    />
                );
            }

            let noActivityReportsForUserDisplay = [];
            for (let k = 0; k < activityListItemModelsForUser.noActivityReports.length; k++) {
                let noActivityReport = activityListItemModelsForUser.noActivityReports[k];
                noActivityReportsForUserDisplay.push(
                    <NoActivityReportListRow
                        key={`no-activity-report-${k}`}
                        noActivityReport={noActivityReport}
                        currentUserCanDeleteNoActivityReport={currentUserCanDeleteNoActivityReport}
                        onDelete={onDeleteNoActivityReport}
                    />
                );
            }

            userRows.push(
                <div
                    key={`user-row-${i}`}
                    className="user-activity-row"
                >
                    <div className="user-activity-row-header">
                        <div className="user-activity-row-header-content">
                            <img
                                className="user-activity-row-user-icon"
                                src="/static/images/users-icon-light-gray.svg"
                            />
                            <div
                                className="user-activity-row-name-text">{activityListItemModelsForUser.user.person.name}</div>
                            <div
                                className="user-activity-row-phone">{PhoneUtility.dbPhoneToParenthesesDisplayPhone(activityListItemModelsForUser.user.person.phone)}</div>
                        </div>
                    </div>

                    <div className="user-activity-row-body">
                        {dispatchOrdersForUserDisplay}
                        {noActivityReportsForUserDisplay}
                    </div>
                </div>
            );
        }

        return userRows;
    }

    if (dataStore.data().lastRefreshTimestamp) {
        return (
            <div className="common-list-page dispatch-orders-list-page">
                <div className="common-list-page-filters-header">
                    <div className="common-list-page-filters-header-content">
                        <div className="common-list-page-filters-header-content-left">
                            <div className="common-list-page-select-filter-container status-filter-container">
                                <img
                                    className="common-list-page-filter-icon status-filter-icon"
                                    src="/static/images/status-icon-green.svg"
                                />
                                <Select
                                    className="common-list-page-select-filter"
                                    classNamePrefix="common-list-page-select-filter"
                                    options={dataStore.data().statusOptions}
                                    value={SelectUtility.selectOptionForId(
                                        '' + dataStore.data().filterStatusId,
                                        dataStore.data().statusOptions
                                    )}
                                    placeholder="Status"
                                    isClearable={true}
                                    onChange={(option) => handleStatusChange(option as SelectOption | null)}
                                    theme={(theme) => ({
                                        ...theme,
                                        borderRadius: 0,
                                        colors: {
                                            ...theme.colors,
                                            text: '#575A4B',
                                            primary25: '#8FCB9B25',
                                            primary50: '#8FCB9B50',
                                            primary: '#8FCB9B',
                                        },
                                    })}
                                    components={{
                                        DropdownIndicator: (dropdownIndicatorProps) => (
                                            <div
                                                className="common-list-page-select-filter-caret-container"
                                                {...(dropdownIndicatorProps.innerProps || {})}
                                            >
                                                <img
                                                    className="common-list-page-select-filter-caret"
                                                    src="/static/images/caret-textcolor.svg"
                                                />
                                            </div>
                                        ),
                                        ClearIndicator: (clearIndicatorProps) => (
                                            <div
                                                className="common-list-page-select-close-container"
                                                {...(clearIndicatorProps.innerProps || {})}
                                            >
                                                <img
                                                    className="common-list-page-select-close"
                                                    src="/static/images/close-textcolor.svg"
                                                />
                                            </div>
                                        ),
                                    }}
                                />
                            </div>
                            <div className="common-list-page-select-filter-container job-filter-container">
                                <img
                                    className="common-list-page-filter-icon job-filter-icon"
                                    src="/static/images/location-pin-green.svg"
                                />
                                <Select
                                    className="common-list-page-select-filter"
                                    classNamePrefix="common-list-page-select-filter"
                                    options={dataStore.data().jobOptions}
                                    value={SelectUtility.selectOptionForId(
                                        '' + dataStore.data().filterJobId,
                                        dataStore.data().jobOptions
                                    )}
                                    placeholder="Job"
                                    isClearable={true}
                                    onChange={(option) => handleJobChange(option as SelectOption | null)}
                                    theme={(theme) => ({
                                        ...theme,
                                        borderRadius: 0,
                                        colors: {
                                            ...theme.colors,
                                            text: '#575A4B',
                                            primary25: '#8FCB9B25',
                                            primary50: '#8FCB9B50',
                                            primary: '#8FCB9B',
                                        },
                                    })}
                                    components={{
                                        DropdownIndicator: (dropdownIndicatorProps) => (
                                            <div
                                                className="common-list-page-select-filter-caret-container"
                                                {...(dropdownIndicatorProps.innerProps || {})}
                                            >
                                                <img
                                                    className="common-list-page-select-filter-caret"
                                                    src="/static/images/caret-textcolor.svg"
                                                />
                                            </div>
                                        ),
                                        ClearIndicator: (clearIndicatorProps) => (
                                            <div
                                                className="common-list-page-select-close-container"
                                                {...(clearIndicatorProps.innerProps || {})}
                                            >
                                                <img
                                                    className="common-list-page-select-close"
                                                    src="/static/images/close-textcolor.svg"
                                                />
                                            </div>
                                        ),
                                    }}
                                />
                            </div>
                            <div className="common-list-page-select-filter-container broker-filter-container">
                                <img
                                    className="common-list-page-filter-icon broker-filter-icon"
                                    src="/static/images/brokers-icon-green.svg"
                                />
                                <Select
                                    className="common-list-page-select-filter"
                                    classNamePrefix="common-list-page-select-filter"
                                    options={dataStore.data().brokerOptions}
                                    value={SelectUtility.selectOptionForId(
                                        '' + dataStore.data().filterBrokerId,
                                        dataStore.data().brokerOptions
                                    )}
                                    placeholder="Broker"
                                    isClearable={true}
                                    onChange={(option) => handleBrokerChange(option as SelectOption | null)}
                                    theme={(theme) => ({
                                        ...theme,
                                        borderRadius: 0,
                                        colors: {
                                            ...theme.colors,
                                            text: '#575A4B',
                                            primary25: '#8FCB9B25',
                                            primary50: '#8FCB9B50',
                                            primary: '#8FCB9B',
                                        },
                                    })}
                                    components={{
                                        DropdownIndicator: (dropdownIndicatorProps) => (
                                            <div
                                                className="common-list-page-select-filter-caret-container"
                                                {...(dropdownIndicatorProps.innerProps || {})}
                                            >
                                                <img
                                                    className="common-list-page-select-filter-caret"
                                                    src="/static/images/caret-textcolor.svg"
                                                />
                                            </div>
                                        ),
                                        ClearIndicator: (clearIndicatorProps) => (
                                            <div
                                                className="common-list-page-select-close-container"
                                                {...(clearIndicatorProps.innerProps || {})}
                                            >
                                                <img
                                                    className="common-list-page-select-close"
                                                    src="/static/images/close-textcolor.svg"
                                                />
                                            </div>
                                        ),
                                    }}
                                />
                            </div>
                            <div className="common-list-page-select-filter-container foreman-filter-container">
                                <img
                                    className="common-list-page-filter-icon foreman-filter-icon"
                                    src="/static/images/users-icon-green.svg"
                                />
                                <Select
                                    className="common-list-page-select-filter"
                                    classNamePrefix="common-list-page-select-filter"
                                    options={dataStore.data().foremanOptions}
                                    value={SelectUtility.selectOptionForId(
                                        '' + dataStore.data().filterForemanUserId,
                                        dataStore.data().foremanOptions
                                    )}
                                    placeholder="Foreman"
                                    isClearable={true}
                                    onChange={(option) => handleForemanChange(option as SelectOption | null)}
                                    theme={(theme) => ({
                                        ...theme,
                                        borderRadius: 0,
                                        colors: {
                                            ...theme.colors,
                                            text: '#575A4B',
                                            primary25: '#8FCB9B25',
                                            primary50: '#8FCB9B50',
                                            primary: '#8FCB9B',
                                        },
                                    })}
                                    components={{
                                        DropdownIndicator: (dropdownIndicatorProps) => (
                                            <div
                                                className="common-list-page-select-filter-caret-container"
                                                {...(dropdownIndicatorProps.innerProps || {})}
                                            >
                                                <img
                                                    className="common-list-page-select-filter-caret"
                                                    src="/static/images/caret-textcolor.svg"
                                                />
                                            </div>
                                        ),
                                        ClearIndicator: (clearIndicatorProps) => (
                                            <div
                                                className="common-list-page-select-close-container"
                                                {...(clearIndicatorProps.innerProps || {})}
                                            >
                                                <img
                                                    className="common-list-page-select-close"
                                                    src="/static/images/close-textcolor.svg"
                                                />
                                            </div>
                                        ),
                                    }}
                                />
                            </div>
                        </div>
                        <div className="common-list-page-filters-header-content-right">
                            <DelayedSearchBar
                                value={dataStore.data().searchText}
                                performSearch={handleSearchChange}
                                placeholder="Search orders"
                            />
                        </div>
                    </div>
                </div>
                <div className="common-list-page-header">
                    <div className="common-list-page-header-content">
                        <div className="common-list-page-header-content-left">
                            <DateControl
                                dateString={dataStore.data().dateString}
                                onPreviousDate={handlePreviousDate}
                                onNextDate={handleNextDate}
                                onDateChange={handleFilterDateChange}
                            />
                        </div>
                        <div className="common-list-page-header-content-right">
                            <div
                                className="icon-button add-button"
                                onClick={goToAddDispatchOrderPage}
                            >
                                <div className="icon-button-content">
                                    <img
                                        className="icon-button-image"
                                        src="/static/images/plus-green.svg"
                                    />
                                    <div className="icon-button-text">Add Activity</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="common-list-page-content">
                    <ConditionalRenderer
                        if={dataStore.data().loading}
                        renderer={() => <OverlayLoadingIndicator/>}
                    />
                    {(() => {
                        if (dataStore.data().dispatchOrdersList.length > 0 || dataStore.data().noActivityReportsList.length > 0) {
                            return (
                                <div className="dispatch-orders-list-content">
                                    {renderDispatchOrdersListDisplay()}
                                </div>
                            );
                        } else {
                            return (
                                <div className="common-empty-list-message-container">
                                    <div className="common-empty-list-message">No dispatch orders found.</div>
                                </div>
                            );
                        }
                    })()}
                </div>
            </div>
        );
    } else {
        return (
            <OverlayLoadingIndicator/>
        );
    }
};

let NoActivityReportListRow = function (props: {
    noActivityReport: NoActivityReport,
    currentUserCanDeleteNoActivityReport: boolean,
    onDelete: (noActivityReportId: number) => void
}) {
    return (
        <div
            className="no-activity-report-list-row ticket-list-row"
            data-no-activity-report-id={props.noActivityReport.id}
        >
            <div className="no-activity-report-list-row-content">
                <div className="ticket-list-row-left-edge">
                    <div className="ticket-list-row-left-edge-punch"></div>
                </div>
                <div className="ticket-list-row-body">
                    <div className="no-activity-report-row-cell no-activity-report-row-job-number-placeholder-cell">
                        <div className="no-activity-report-row-job-number-placeholder">
                            <div className="no-activity-report-row-job-number-placeholder-dash"></div>
                        </div>
                    </div>
                    <div className="no-activity-report-row-cell no-activity-report-row-foreman-name-cell">
                        <div className="no-activity-report-row-foreman-name">
                            {props.noActivityReport.user.person.name}
                        </div>
                    </div>
                    <div className="no-activity-report-row-cell no-activity-report-row-message-cell">
                        <div className="no-activity-report-row-message">No activity needed today.</div>
                    </div>
                    <div className="no-activity-report-row-spacer"></div>
                    <ConditionalRenderer
                        if={props.currentUserCanDeleteNoActivityReport}
                        renderer={() => (
                            <div className="no-activity-report-row-cell no-activity-report-row-trash-cell">
                                <ConfirmDeleteButton
                                    iconSrc="/static/images/trash-textcolor.svg"
                                    confirmingIconSrc="/static/images/trash-white.svg"
                                    confirmText="CONFIRM DELETE"
                                    onConfirm={() => props.onDelete(props.noActivityReport.id)}
                                />
                            </div>
                        )}
                    />
                </div>
            </div>
        </div>
    );
};
